import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Camera,
  CalendarDays,
  Package,
  UserCog,
  MapPin,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Heart,
  Megaphone,
  BookImage,
  Printer,
  Crown
} from 'lucide-react';
import { Button } from '../ui/button';
import FloatingChatWidget from '../FloatingChatWidget';

const API = RUNTIME_BACKEND_URL;

// Fotoğrafçı menüsü
const navigation = [
  { name: 'Dashboard', href: '/panel', icon: LayoutDashboard },
  { name: 'Müşteriler', href: '/panel/musteriler', icon: Users },
  { name: 'Takvim', href: '/panel/takvim', icon: CalendarDays },
  { name: 'Paketler', href: '/panel/paketler', icon: Package },
  { name: 'Ekip', href: '/panel/ekip', icon: UserCog },
  { name: 'Albüm Şablonları', href: '/panel/album-sablonlari', icon: BookImage },
  { name: 'Baskı Siparişleri', href: '/panel/baski-siparisleri', icon: Printer },
  { name: 'Bildirimler', href: '/panel/bildirimler', icon: Bell },
  { name: 'Ayarlar', href: '/panel/ayarlar', icon: Settings },
];

// Tarih formatlama - Gün Ay Yıl Saat:Dakika
const formatDateTime = () => {
  const now = new Date();
  const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  const day = days[now.getDay()];
  const date = now.getDate();
  const month = months[now.getMonth()];
  const year = now.getFullYear();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  
  return `${day}, ${date} ${month} ${year} ${hours}:${minutes}`;
};

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatDateTime());
  const [siteSettings, setSiteSettings] = useState({ site_title: 'WeddingTakip', logo_url: null, footer_text: '' });
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const { user, logout, updateTheme } = useAuthStore();
  const navigate = useNavigate();

  // Saat güncelleme
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(formatDateTime());
    }, 60000); // Her dakika güncelle
    return () => clearInterval(interval);
  }, []);

  // Site ayarları ve duyuru yükleme
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Site ayarları
        const settingsRes = await fetch(`${API}/api/site-settings`);
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSiteSettings(data);
          // Sayfa başlığını güncelle
          if (data.site_title) {
            document.title = data.site_title;
          }
        }
        
        // Duyurular
        const annRes = await fetch(`${API}/api/announcements/active`);
        if (annRes.ok) {
          const data = await annRes.json();
          setAnnouncements(data);
        }
      } catch (error) {
        console.error('Settings load error:', error);
      }
    };
    loadSettings();
  }, []);

  // Duyuru kapat
  const dismissAnnouncement = (id) => {
    setDismissedAnnouncements(prev => [...prev, id]);
  };

  // Görünür duyurular
  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

  const handleLogout = () => {
    logout();
    navigate('/giris');
  };

  const toggleTheme = () => {
    const newTheme = user?.theme === 'dark' ? 'light' : 'dark';
    updateTheme(newTheme);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r border-border transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-2">
              {siteSettings.logo_url ? (
                <img 
                  src={`${API}${siteSettings.logo_url}`} 
                  alt={siteSettings.site_title}
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <Heart className="h-6 w-6 text-primary" />
              )}
              <span className="text-xl font-semibold gradient-text">{siteSettings.site_title}</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/panel'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.full_name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="flex-1 justify-center"
                data-testid="theme-toggle-btn"
              >
                {user?.theme === 'dark' ? (
                  <Sun className="h-4 w-4 mr-2" />
                ) : (
                  <Moon className="h-4 w-4 mr-2" />
                )}
                {user?.theme === 'dark' ? 'Açık' : 'Koyu'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex-1 justify-center text-destructive hover:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Announcements */}
        {visibleAnnouncements.length > 0 && (
          <div className="bg-primary/10 border-b border-primary/20">
            {visibleAnnouncements.map((ann) => (
              <div key={ann.id} className="flex items-center justify-between px-4 py-2 gap-4">
                <div className="flex items-center gap-3">
                  <Megaphone className="h-4 w-4 text-primary flex-shrink-0" />
                  <div>
                    <span className="font-medium text-sm">{ann.title}:</span>
                    <span className="text-sm ml-1">{ann.content}</span>
                  </div>
                </div>
                <button 
                  onClick={() => dismissAnnouncement(ann.id)}
                  className="text-muted-foreground hover:text-foreground p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-card/80 backdrop-blur-sm px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground hover:text-foreground"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          {/* Current Date/Time */}
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
            <span>{currentTime}</span>
          </div>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.company_name || user?.full_name}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Footer */}
        {siteSettings.footer_text && (
          <footer className="border-t border-border bg-card/50 px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground">{siteSettings.footer_text}</p>
          </footer>
        )}
      </div>

      {/* Floating Chat Widget (albümcü sohbetleri) */}
      <FloatingChatWidget />
    </div>
  );
}
